import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api, formatBRL, formatDateBR } from "../lib/api";
import { Search, Building2, Plus, Loader2, ArrowRight } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import StatusBadge from "../components/StatusBadge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import RepresentativesSection from "../components/RepresentativesSection";
import { isPJDocument, maskCEP } from "../lib/representatives";
import { fetchCepAddress, mergeCepAddress, normalizeCep } from "../lib/cep";

const UF_LIST = ["AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT","PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO"];

function maskCNPJ(v) {
  return v.replace(/\D/g, "")
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2")
    .slice(0, 18);
}

export default function Clients() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCreate = !!user && (user.role === "admin" || user.role === "operator");
  const [cedentes, setCedentes] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  // New cedente form
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ razao_social: "", cnpj: "", nome_fantasia: "", cidade: "", uf: "", contato_email: "", contato_telefone: "" });

  // CEP-first no Novo Cedente: campo de BUSCA (lookup-only, NÃO persistido —
  // o cedente só guarda cidade/uf; endereço completo é escopo do item D).
  // Mesma semântica do form de representante (mergeCepAddress): cada CEP
  // completo DIFERENTE do último consultado re-busca e re-preenche cidade/UF
  // (digitado manualmente nesta sessão nunca é sobrescrito); falha → hint,
  // nunca bloqueia o submit.
  const [cepBusca, setCepBusca] = useState("");
  const [cepStatus, setCepStatus] = useState("idle"); // "idle" | "loading" | "not_found"
  const cepRequestIdRef = useRef(0);
  const lastLookupCepRef = useRef(null);
  const manuallyEditedRef = useRef(new Set());

  const resetNewCedenteForm = () => {
    setForm({ razao_social: "", cnpj: "", nome_fantasia: "", cidade: "", uf: "", contato_email: "", contato_telefone: "" });
    setCepBusca("");
    setCepStatus("idle");
    cepRequestIdRef.current += 1; // invalida consulta em voo
    lastLookupCepRef.current = null;
    manuallyEditedRef.current = new Set();
  };

  const handleCepBuscaChange = async (raw) => {
    const masked = maskCEP(raw);
    setCepBusca(masked);
    const digits = normalizeCep(masked);
    if (digits.length !== 8) {
      lastLookupCepRef.current = null;
      setCepStatus("idle");
      return;
    }
    if (digits === lastLookupCepRef.current) return;
    lastLookupCepRef.current = digits;
    const requestId = ++cepRequestIdRef.current;
    setCepStatus("loading");
    const address = await fetchCepAddress(digits);
    if (requestId !== cepRequestIdRef.current) return; // resposta obsoleta
    if (!address) {
      setCepStatus("not_found");
      return;
    }
    setCepStatus("idle");
    setForm((f) => mergeCepAddress(f, address, {
      manuallyEdited: manuallyEditedRef.current,
      fields: { cidade: "city", uf: "state" },
    }));
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/cedentes");
      setCedentes(data);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    (async () => {
      const { data } = await api.get(`/cedentes/${selected.id}`);
      setDetail(data);
    })();
  }, [selected]);

  const filtered = useMemo(() => {
    if (!search) return cedentes;
    const s = search.toLowerCase();
    return cedentes.filter(
      (c) =>
        c.razao_social.toLowerCase().includes(s) ||
        c.nome_fantasia?.toLowerCase().includes(s) ||
        c.cnpj.includes(s)
    );
  }, [cedentes, search]);

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <span className="zemo-overline">Clientes</span>
          <h1 className="mt-1.5 text-3xl sm:text-[34px] font-semibold tracking-tight text-zinc-900">
            Cedentes/Clientes
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            Lista de cedentes do parceiro.
          </p>
        </div>
        {canCreate && (
          <Button
            className="bg-[#0055FF] hover:bg-[#0044CC] text-white h-10 font-semibold"
            onClick={() => { resetNewCedenteForm(); setShowNew(true); }}
            data-testid="new-cedente-button"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Novo Cedente
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input
          placeholder="Buscar por razão social, fantasia ou CNPJ…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 h-10"
          data-testid="cedentes-search-input"
        />
      </div>

      {/* Grid of cedentes */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="cedentes-grid">
        {filtered.map((c) => (
          <button
            key={c.id}
            onClick={() => setSelected(c)}
            className="text-left zemo-card zemo-card-hover p-5 group"
            data-testid={`cedente-card-${c.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="h-10 w-10 rounded-md bg-zinc-100 border border-[var(--zemo-border)] flex items-center justify-center text-zinc-700 group-hover:bg-[#0055FF]/10 group-hover:text-[#0055FF] group-hover:border-[#0055FF]/30 transition-colors">
                <Building2 className="h-5 w-5" />
              </div>
              <StatusBadge status={c.status} />
            </div>
            <h3 className="mt-3 font-semibold text-zinc-900 leading-snug">{c.razao_social}</h3>
            <div className="text-[12px] text-zinc-500 font-mono-num mt-0.5">{c.cnpj}</div>
            <div className="mt-3 pt-3 border-t border-[var(--zemo-border)] grid grid-cols-2 gap-2 text-[12px]">
              <div>
                <div className="zemo-overline text-[10px]">Limite</div>
                <div className="font-semibold font-mono-num text-zinc-900 mt-0.5">
                  {formatBRL(c.limite_aprovado)}
                </div>
              </div>
              <div>
                <div className="zemo-overline text-[10px]">Score</div>
                <div className="font-semibold font-mono-num text-zinc-900 mt-0.5">{c.score}</div>
              </div>
            </div>
            <div className="mt-2 text-[11px] text-zinc-500">
              {c.cidade} · {c.uf}
            </div>
          </button>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-zinc-500 py-10">
            Nenhum cedente encontrado.
          </div>
        )}
      </div>

      {/* Detail sheet */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="cedente-detail-sheet">
          <SheetHeader>
            <SheetTitle className="text-xl">{selected?.razao_social}</SheetTitle>
          </SheetHeader>
          {detail && (
            <div className="mt-6 space-y-6">
              <div className="grid grid-cols-2 gap-3">
                {[
                  ["CNPJ", detail.cnpj],
                  ["Nome Fantasia", detail.nome_fantasia || "—"],
                  ["Cidade/UF", `${detail.cidade} · ${detail.uf}`],
                  ["Status", detail.status === "active" ? "Ativo" : "Inativo"],
                  ["Limite aprovado", formatBRL(detail.limite_aprovado)],
                  ["Score", String(detail.score)],
                  ["Cadastrado em", formatDateBR(detail.created_at)],
                ].map(([k, v]) => (
                  <div key={k} className="p-3 border border-[var(--zemo-border)] rounded-md">
                    <div className="zemo-overline">{k}</div>
                    <div className="mt-1 font-semibold text-zinc-900 text-sm">{v}</div>
                  </div>
                ))}
              </div>

              <Button
                className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold"
                onClick={() => { setSelected(null); navigate(`/operacoes/nova?cedente_id=${detail.id}`); }}
                data-testid="cedente-new-operation-btn"
              >
                <ArrowRight className="h-4 w-4 mr-2" /> Nova Operacao para este Cedente
              </Button>

              {/* Sócios/Representantes — quem assina os contratos do cedente PJ
                  (item 22). Cedente PF assina ele mesmo: só a nota informativa. */}
              {isPJDocument(detail.cnpj) ? (
                <RepresentativesSection cedenteId={detail.id} canManage={canCreate} />
              ) : (
                <div className="rounded-md p-3 border border-zinc-200 bg-zinc-50 text-[13px] text-zinc-600" data-testid="representatives-pf-note">
                  Cedente pessoa física assina os contratos ele mesmo — não é
                  necessário cadastrar representantes.
                </div>
              )}

              <div>
                <h4 className="text-sm font-semibold mb-2">Operacoes recentes</h4>
                <div className="border border-[var(--zemo-border)] rounded-md divide-y divide-[var(--zemo-border)]">
                  {(detail.operations || []).slice(0, 8).map((op) => (
                    <div key={op.id} className="px-3 py-2.5 flex items-center justify-between text-sm">
                      <div>
                        <div className="font-mono-num font-semibold">{op.numero}</div>
                        <div className="text-[11px] text-zinc-500">{op.product_nome}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono-num font-semibold">{formatBRL(op.valor)}</div>
                        <div className="mt-0.5">
                          <StatusBadge status={op.status} />
                        </div>
                      </div>
                    </div>
                  ))}
                  {(!detail.operations || detail.operations.length === 0) && (
                    <div className="px-3 py-6 text-center text-zinc-500 text-sm">
                      Nenhuma operacao para este cedente ainda.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* New Cedente Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="sm:max-w-lg" data-testid="new-cedente-dialog">
          <DialogHeader>
            <DialogTitle>Cadastrar Novo Cedente</DialogTitle>
            <DialogDescription>Cadastre um cedente vinculado ao seu parceiro. O CNPJ sera validado automaticamente.</DialogDescription>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            setSubmitting(true);
            try {
              await api.post("/cedentes", {
                razao_social: form.razao_social,
                cnpj: form.cnpj,
                nome_fantasia: form.nome_fantasia || null,
                cidade: form.cidade || null,
                uf: form.uf || null,
                contato_email: form.contato_email || null,
                contato_telefone: form.contato_telefone || null,
              });
              toast.success("Cedente cadastrado com sucesso");
              setShowNew(false);
              load();
            } catch (err) {
              // Fail-closed no backend: se a V2 recusar, NADA é criado e o erro chega
              // aqui — nunca sucesso silencioso. detail pode ser string (erro de
              // negócio/V2) ou lista (422 de validação do FastAPI).
              const detail = err?.response?.data?.detail;
              const msg = Array.isArray(detail)
                ? detail.map((d) => d?.msg || JSON.stringify(d)).join("; ")
                : (typeof detail === "string" && detail) || "Falha ao cadastrar cedente";
              toast.error(msg);
            } finally { setSubmitting(false); }
          }} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Razao Social *</Label>
                <Input required value={form.razao_social} onChange={(e) => setForm({...form, razao_social: e.target.value})} placeholder="Empresa LTDA" data-testid="new-cedente-razao" />
              </div>
              <div className="space-y-1.5">
                <Label>CNPJ *</Label>
                <Input required value={form.cnpj} onChange={(e) => setForm({...form, cnpj: maskCNPJ(e.target.value)})} placeholder="00.000.000/0000-00" maxLength={18} className="font-mono-num" data-testid="new-cedente-cnpj" />
              </div>
              <div className="space-y-1.5">
                <Label>Nome Fantasia</Label>
                <Input value={form.nome_fantasia} onChange={(e) => setForm({...form, nome_fantasia: e.target.value})} placeholder="Nome comercial" data-testid="new-cedente-fantasia" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>CEP (busca)</Label>
                <div className="relative">
                  <Input
                    value={cepBusca}
                    onChange={(e) => handleCepBuscaChange(e.target.value)}
                    placeholder="00000-000 — preenche cidade/UF"
                    maxLength={9}
                    className="font-mono-num"
                    aria-busy={cepStatus === "loading"}
                    data-testid="new-cedente-cep"
                  />
                  {cepStatus === "loading" && (
                    <Loader2 className="h-4 w-4 animate-spin text-zinc-400 absolute right-2.5 top-1/2 -translate-y-1/2" aria-hidden="true" />
                  )}
                </div>
                <p aria-live="polite" className="text-[12px] text-zinc-400" data-testid="new-cedente-cep-hint">
                  {cepStatus === "loading" && "Buscando cidade/UF pelo CEP…"}
                  {cepStatus === "not_found" && "CEP não encontrado — preencha manualmente."}
                  {cepStatus === "idle" && "Usado apenas para preencher cidade e UF — o CEP não fica no cadastro."}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Cidade</Label>
                <Input value={form.cidade} onChange={(e) => { manuallyEditedRef.current.add("cidade"); setForm({...form, cidade: e.target.value}); }} placeholder="Sao Paulo" data-testid="new-cedente-cidade" />
              </div>
              <div className="space-y-1.5">
                <Label>UF</Label>
                <Select value={form.uf} onValueChange={(v) => { manuallyEditedRef.current.add("uf"); setForm({...form, uf: v}); }}>
                  <SelectTrigger data-testid="new-cedente-uf"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {UF_LIST.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>E-mail contato</Label>
                <Input type="email" value={form.contato_email} onChange={(e) => setForm({...form, contato_email: e.target.value})} placeholder="contato@empresa.com" />
              </div>
              <div className="space-y-1.5">
                <Label>Telefone</Label>
                <Input value={form.contato_telefone} onChange={(e) => setForm({...form, contato_telefone: e.target.value})} placeholder="+55 11 99999-9999" className="font-mono-num" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowNew(false)}>Cancelar</Button>
              <Button type="submit" disabled={submitting} data-testid="new-cedente-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Cadastrar Cedente
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
